import { compactDecrypt } from 'jose';
const jweKey = import.meta.env.VITE_JWE_KEY;
const base64Key = import.meta.env.VITE_BASE64_KEY;
const VITE_RESETPIN_KEY = import.meta.env.VITE_RESETPIN_KEY;
const SEPARATOR = '|';
const NONCE = import.meta.env.VITE_ENCRYPTION_NONCE;

export const DecryptJweToken = async (jweToken) => {
  try {
    const keyUint8 = new TextEncoder().encode(jweKey);
    const { plaintext } = await compactDecrypt(jweToken, keyUint8);
    const decodedText = new TextDecoder().decode(plaintext);

    try {
      const parsedJson = JSON.parse(decodedText);
      return JSON.stringify(parsedJson, null, 2);
    } catch {
      return decodedText;
    }
  } catch (err) {
    throw new Error('Decryption failed: ' + err.message);
  }
};

// Helper: convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Helper: import AES-GCM key from base64
const importKey = async () => {
  const rawKey = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    'AES-GCM',
    true,
    ['encrypt', 'decrypt']
  );
};

// MW Encrypt function
export const EncryptText = async (data) => {
  const key = await importKey();

  // Generate random 12-byte IV
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // Encode the data as Uint8Array
  const encodedData = new TextEncoder().encode(JSON.stringify(data));

  // Encrypt using AES-GCM
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
      tagLength: 128
    },
    key,
    encodedData
  );

  const encryptedArray = new Uint8Array(encryptedBuffer);

  // AES-GCM appends 16-byte auth tag at the end
  const ciphertext = encryptedArray.slice(0, -16);
  const authTag = encryptedArray.slice(-16);

  return {
    iv: arrayBufferToBase64(iv),
    encryptedMessage: arrayBufferToBase64(ciphertext),
    authTag: arrayBufferToBase64(authTag)
  };
};

// Helper: convert Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};


// MW Decrypt function
export const DecryptText = async (encryptedData) => {
  const key = await importKey();

  const iv = base64ToArrayBuffer(encryptedData.iv);
  const encryptedMessageBuffer = base64ToArrayBuffer(encryptedData.encryptedMessage);
  const authTagBuffer = base64ToArrayBuffer(encryptedData.authTag);

  // Combine the ciphertext and auth tag
  const combinedBuffer = new Uint8Array(
    encryptedMessageBuffer.byteLength + authTagBuffer.byteLength
  );
  combinedBuffer.set(new Uint8Array(encryptedMessageBuffer), 0);
  combinedBuffer.set(new Uint8Array(authTagBuffer), encryptedMessageBuffer.byteLength);

  // Decrypt
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv),
      tagLength: 128
    },
    key,
    combinedBuffer.buffer
  );

  const decoder = new TextDecoder();
  const decryptedText = decoder.decode(decryptedBuffer);

  return JSON.parse(decryptedText);
};

function base64Decoding(input) {
  return new Uint8Array(
    atob(input)
      .split('')
      .map((char) => char.charCodeAt(0))
  );
}

// CMS Decrypt function
export async function CMSEncrypt(data,type) {
  try {
    const nonce = generateFixedNonce();
    let API_KEY;
    if(type==='nonpin'){
      API_KEY=base64Key
    }
    else{
      API_KEY=VITE_RESETPIN_KEY
    }
    const key = await getKey(API_KEY);
    const encodedData = new TextEncoder().encode(JSON.stringify(data));

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce },
      key,
      encodedData
    );

    const encryptedArray = new Uint8Array(encrypted);
    const ciphertext = encryptedArray.slice(0, encryptedArray.length - 16);
    const authTag = encryptedArray.slice(encryptedArray.length - 16);

    const ivBase64 = base64Encode(nonce);
    const encryptedBase64 = base64Encode(ciphertext);
    const authTagBase64 = base64Encode(authTag);

    return `${ivBase64}${SEPARATOR}${encryptedBase64}${SEPARATOR}${authTagBase64}`;
  } catch (error) {
    console.error("Encryption error:", error.message);
    return error.message;
  }
}

// Helpers
function generateFixedNonce() {
  return new TextEncoder().encode(NONCE);
}

async function getKey(key) {
  const rawKey = base64Decode(key);
  return crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

function base64Encode(data) {
  return btoa(String.fromCharCode(...data));
}

function base64Decode(data) {
  try {
    const padded = data.padEnd(data.length + (4 - (data.length % 4)) % 4, "=");
    return new Uint8Array(
      atob(padded)
        .split("")
        .map((char) => char.charCodeAt(0))
    );
  } catch (error) {
    throw new Error("Failed to decode base64: " + error.message);
  }
}

// CMS Decrypt function
export async function CMSDecrypt(encryptedData) {
  try {
    const parts = encryptedData.split(SEPARATOR);
    if (parts.length !== 3) {
      console.error('Invalid encrypted data:', encryptedData);
      throw new Error('Invalid encrypted data format');
    }

    const iv = base64Decoding(parts[0]);
    const encryptedCardNumber = base64Decoding(parts[1]);
    const authTag = base64Decoding(parts[2]);

    const keyBytes = base64Decoding(base64Key);
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // Concatenate ciphertext and auth tag
    const ciphertextWithTag = new Uint8Array(
      encryptedCardNumber.length + authTag.length
    );
    ciphertextWithTag.set(encryptedCardNumber);
    ciphertextWithTag.set(authTag, encryptedCardNumber.length);

    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128,
      },
      key,
      ciphertextWithTag
    );

    let decryptedText = new TextDecoder().decode(decryptedBuffer);
    decryptedText = decryptedText.replace(/^"|"$/g, '');
    return decryptedText;

  } catch (error) {
    console.error('Decryption failed:', error);
    return encryptedData; // fallback
  }
}

